<template>
  <Card id="contact-activity" class="border-gray-200">
    <CardHeader class="flex flex-col gap-4 space-y-0 md:flex-row md:items-center md:justify-between">
      <CardTitle>Activity</CardTitle>
      <CardAction class="m-0">
        <Tabs v-model="activeTab" default-value="all">
          <TabsList variant="minimal" class="flex-wrap">
            <TabsTrigger variant="minimal" value="all">All</TabsTrigger>
            <TabsTrigger variant="minimal" value="emails">Emails</TabsTrigger>
            <TabsTrigger variant="minimal" value="messages">Messages</TabsTrigger>
            <TabsTrigger variant="minimal" value="calls">Calls</TabsTrigger>
            <TabsTrigger variant="minimal" value="notes">Notes</TabsTrigger>
            <TabsTrigger variant="minimal" value="timeline">Timeline</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardAction>
    </CardHeader>
    <CardContent class="pt-0">
      <NoteInput
        ref="noteInputRef"
        v-model:content="contactNote.content"
        v-model:note-type="contactNote.noteType"
        @close="resetNote"
      >
        <template #actions="{ close }">
          <Button
            class="min-w-28"
            :class="
              isNoteValid ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-400 text-white hover:bg-gray-400'
            "
            :disabled="!isNoteValid"
            @click="createNote()"
          >
            Save
            <LocalLoadingSpinner />
          </Button>
          <Button variant="outline" class="min-w-28" @click="close"> Cancel </Button>
        </template>
      </NoteInput>
    </CardContent>
    <Separator class="mt-2" />
    <CardContent class="pt-6">
      <ul v-if="filteredActivity.length > 0" role="list" class="space-y-3">
        <li
          v-for="(item, index) of filteredActivity.slice(0, notesIndex)"
          :key="`${item.categories}-${item.id ?? index}`"
          class="last:pb-2 [&:last-child_.timeline-line]:hidden"
        >
          <ContactActivityPreview :item="item" @delete-interview-clicked="emit('deleteInterviewClicked')" />
        </li>
      </ul>
      <div v-else class="py-4 text-center text-gray-500">No notes or activities found</div>
      <Button
        v-if="filteredActivity.length > notesIndex"
        variant="outline"
        class="mx-auto mt-4 flex text-gray-700"
        @click="notesIndex = notesIndex + 999999"
      >
        Load more activity
      </Button>
      <Button
        v-else-if="filteredActivity.length > 5"
        variant="ghost"
        class="mx-auto mt-4 flex text-gray-500"
        @click="notesIndex = 5"
      >
        Show Less
      </Button>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { Api } from '@/api';
import Button from '@/components/ui/button/Button.vue';
import type { ContactDetail } from '../ContactDetail';
import NoteInput from '../NoteInput.vue';
import ContactActivityPreview from './ContactActivityPreview.vue';
import type { ActivityItem, ActivityTabCategory } from './Transformers/useActivityTransformer';
import { Separator } from '@/components/ui/separator';
import { getNoteType, type NoteTypeOption } from '../noteTypes';

type ContactActivityProps = {
  contact: ContactDetail;
  activities: ActivityItem[];
};

type ContactNoteInput = {
  content: string;
  noteType: NoteTypeOption;
};

const { contact, activities } = defineProps<ContactActivityProps>();
const loadingSpinnerStore = useLoadingSpinnerStore();

const contactNote = ref<ContactNoteInput>({
  content: '',
  noteType: 'General note',
});
const notesIndex = ref(5);
const noteInputRef = ref<InstanceType<typeof NoteInput>>();

const activeTab = ref<'all' | 'emails' | 'messages' | 'calls' | 'notes' | 'timeline'>('all');

const emit = defineEmits(['activityCreated', 'activityDeleted', 'deleteInterviewClicked']);

const filteredActivity = computed(() => {
  if (activeTab.value === 'all') return activities;
  return activities.filter(item => item.categories.includes(activeTab.value as ActivityTabCategory));
});

const isNoteValid = computed(() => {
  const normalizedContent = contactNote.value.content
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return normalizedContent.length > 0;
});

watch(activeTab, () => {
  notesIndex.value = 5;
});

const resetNote = () => {
  contactNote.value.content = '';
  contactNote.value.noteType = 'General note';
};

const createNote = async () => {
  if (!isNoteValid.value) return;

  loadingSpinnerStore.showLocalLoadingSpinner();
  const response = await Api.ContactNotes.createContactNote({
    contact_id: contact.id,
    content: contactNote.value.content,
    followup_at: '',
    note_type: getNoteType(contactNote.value.noteType),
  });

  loadingSpinnerStore.hideLocalLoadingSpinner();
  if (response?.status != 'error') {
    noteInputRef.value?.close();
    resetNote();
    emit('activityCreated');
    toast.success('Note created', {
      action: { label: 'Dismiss' },
      description:
        'Your candidate note was successfully created.  Use the buttons below to view your new contact, or dismiss this alert.',
    });
  } else {
    toast.error('Note create error', {
      action: { label: 'Dismiss' },
      description: response?.message,
    });
  }
};
</script>
